export const LS_API_URL = 'apiUrl';

export const overrideApiUrl = (apiUrl: string) => {
  localStorage.setItem(LS_API_URL, apiUrl);
  window.location.reload();
};

export const getOverrideApiUrl = () => {
  return localStorage.getItem(LS_API_URL);
};

// Dev-only auth bypass. Two activation paths:
//  1. .env: REACT_APP_DEV_BYPASS_AUTH=true (development mode only)
//  2. localStorage: DEV_BYPASS_AUTH='1' (toggled via the dev pill, works in
//     preview/prod too once the pill is unlocked).
// Read at runtime — never cache at module load — so toggling the pill takes
// effect on the next render/reload without a rebuild.
export const LS_DEV_BYPASS_AUTH = 'DEV_BYPASS_AUTH';

export const isDevBypassAuth = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (
    import.meta.env.REACT_APP_DEV_BYPASS_AUTH === 'true' &&
    import.meta.env.MODE === 'development'
  ) {
    return true;
  }
  return localStorage.getItem(LS_DEV_BYPASS_AUTH) === '1';
};

export const setDevBypassAuth = (enabled: boolean) => {
  if (enabled) {
    localStorage.setItem(LS_DEV_BYPASS_AUTH, '1');
  } else {
    localStorage.removeItem(LS_DEV_BYPASS_AUTH);
  }
};

// Citizen auth keys live in plain strings across User.store / Property.store /
// api/client. When the dev pill switches tenant or backend, the old citizen
// token is bound to the previous tenant — leaving it in place makes the new
// tenant's anonymous-login path skip, and tenant-scoped requests (property
// search, etc.) silently return empty against the wrong tenant.
export const clearCitizenAuth = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('AUTH_TOKEN');
  localStorage.removeItem('AUTH_TOKEN_EXPIRY');
  localStorage.removeItem('AUTH_PROPERTY_ID');
  localStorage.removeItem('AUTH_PROPERTY_NAME');
};

// Single read accessor for the citizen's selected property id. Centralizes the
// magic-string key shared by Property.store and CriptioLogin's MitID/NemID
// redirect builders. Returns null (not '') when unset so a falsy check is safe.
export const getAuthPropertyId = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('AUTH_PROPERTY_ID');
};

// Mock hauler session keys. EmailOtpLogin sets HAULER_MODE='true' after the
// (mocked) OTP verify; HAULER_COMPANY is the id of a hauler company in the
// devMock registry, used by the property interceptor to pick the right
// fixture set; HAULER_EMAIL preserves the login email across reloads for the
// `applyHaulerOverride` user-store rewrite. All three live in sessionStorage,
// not localStorage, so the hauler "session" dies with the browser tab — opening
// a fresh tab on `/self-service` always boots as a citizen even if a previous
// tab was a hauler. Swap all of this out once the backend ships real hauler
// endpoints + a proper token.
export const SS_HAULER_MODE = 'HAULER_MODE';
export const SS_HAULER_COMPANY = 'HAULER_COMPANY';
export const SS_HAULER_EMAIL = 'HAULER_EMAIL';

// True only when (a) the OTP-set flag is present in this tab AND (b) we're
// currently rendering a /hauler URL. The path gate prevents a leftover flag
// from one tab/route bleeding into citizen pages — `applyHaulerOverride`,
// the devMock hauler matchers, the User.store gate, all silently no-op
// outside the hauler shell.
export const isHaulerSession = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (sessionStorage.getItem(SS_HAULER_MODE) !== 'true') return false;
  return window.location.pathname.startsWith('/hauler');
};

export const getHaulerCompanyId = (): string | null => {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(SS_HAULER_COMPANY);
};

export const getHaulerEmail = (): string | null => {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(SS_HAULER_EMAIL);
};

export const clearHaulerSession = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(SS_HAULER_MODE);
  sessionStorage.removeItem(SS_HAULER_COMPANY);
  sessionStorage.removeItem(SS_HAULER_EMAIL);
};
